import type {
  RealtyCity,
  RealtySearchRequest,
} from 'shared/api/kyrgyzstan/search'
import { formatDateForApi } from 'shared/lib/formatDate'

import { GuestsLimits } from '../config/guests'
import type { GuestsState } from '../model'

export const getParamValue = <T>(
  paramValue: T | undefined,
  storeValue: T | undefined,
): T | undefined => (paramValue !== undefined ? paramValue : storeValue)

export const formatDateForRequest = (date?: string): string | undefined =>
  date ? formatDateForApi(date) : undefined

export const normalizeChildrenAges = (guests?: GuestsState): number[] => {
  if (!guests || guests.children <= 0) return []

  return Array.from(
    { length: guests.children },
    (_, index) => guests.childrenAges[index] ?? GuestsLimits.childAge.default,
  )
}

interface PrepareRequestParamsOptions {
  params?: Partial<RealtySearchRequest>
  page: number
  pageSize: number
  sort: string
  start_date?: string
  end_date?: string
  cities: RealtyCity[]
  visitorsCount?: number
  guestsCount?: number
  childrenAges?: number[]
  realty_type_ids?: number[]
  price_min?: number
  price_max?: number
}

export const prepareRequestParams = (
  options: PrepareRequestParamsOptions,
): RealtySearchRequest | null => {
  const {
    params,
    page,
    pageSize,
    sort,
    start_date: startDateSource,
    end_date: endDateSource,
    cities,
    visitorsCount,
    guestsCount,
    childrenAges,
    realty_type_ids: realtyTypeIds,
    price_min: priceMinSource,
    price_max: priceMaxSource,
  } = options

  const requestPage = params?.page || page
  const startDate = formatDateForRequest(params?.start_date || startDateSource)
  const endDate = formatDateForRequest(params?.end_date || endDateSource)
  const cityIds = getParamValue(
    params?.city_id,
    cities.map((city) => city.id),
  )
  const requestVisitorsCount = getParamValue(
    params?.visitors_count,
    visitorsCount ?? guestsCount,
  )
  const requestChildrenAges = getParamValue(params?.children_ages, childrenAges)
  const priceMin = getParamValue(params?.price_min, priceMinSource)
  const priceMax = getParamValue(params?.price_max, priceMaxSource)

  const realtyTypeId = (() => {
    const paramTypeId = params?.realty_type_id
    if (paramTypeId !== undefined) {
      return paramTypeId
    }

    if (realtyTypeIds && realtyTypeIds.length > 0) {
      return realtyTypeIds.join(',')
    }

    return undefined
  })()

  if (
    !startDate ||
    !endDate ||
    cityIds === undefined ||
    (Array.isArray(cityIds) && cityIds.length === 0) ||
    requestVisitorsCount === undefined
  ) {
    return null
  }

  return {
    page: requestPage,
    page_size: params?.page_size || pageSize,
    sort: getParamValue(params?.sort, sort),
    start_date: startDate,
    end_date: endDate,
    city_id: cityIds,
    visitors_count: requestVisitorsCount,
    ...(requestChildrenAges?.length
      ? { children_ages: requestChildrenAges }
      : {}),
    realty_type_id: realtyTypeId,
    price_min: priceMin,
    price_max: priceMax,
  }
}

export type ChildrenSearchParamsStore = {
  sort: string
  start_date?: string
  end_date?: string
  cities: RealtyCity[]
  visitorsCount?: number
  childrenAges?: number[]
  realty_type_ids?: number[]
  price_min?: number
  price_max?: number
}

export function getChildrenSearchParamsFromStore(
  store: ChildrenSearchParamsStore,
): Omit<RealtySearchRequest, 'page' | 'page_size'> | null {
  const requestParams = prepareRequestParams({
    page: 1,
    pageSize: 10,
    sort: store.sort,
    start_date: store.start_date,
    end_date: store.end_date,
    cities: store.cities,
    visitorsCount: store.visitorsCount,
    childrenAges: store.childrenAges,
    realty_type_ids: store.realty_type_ids,
    price_min: store.price_min,
    price_max: store.price_max,
  })
  if (!requestParams) return null

  const { page, page_size: pageSize, ...rest } = requestParams
  return rest
}
